import { z } from 'zod'
import { type StrictFeatureId } from '../feature/featureIds'
import { keys } from '../../util/object'

export type PermissionDefinition = {
  label: string
  category: string
  action: string
  /** gets appended to the action, make sure to include spacing and/or punctuation (if applicable) */
  additionalDescription?: string
  deprecated?: boolean
  /** If true, this permission is for internal Terros users only */
  internal?: boolean
  /** The scopes that can be targeted with this permission */
  entities: readonly PermissionEntityScope[]
  /** Feature flag that must be enabled for this permission to be visible in the UI */
  featureId?: StrictFeatureId
}

export const PERMISSION_ENTITY_SCOPES = ['none', 'self', 'downline', 'withinRestrictions', 'company'] as const
export const PermissionEntityScopeSchema = z.enum(PERMISSION_ENTITY_SCOPES)
export type PermissionEntityScope = z.infer<typeof PermissionEntityScopeSchema>
export const isPermissionEntityScope = (value: unknown): value is PermissionEntityScope => {
  return PermissionEntityScopeSchema.safeParse(value).success
}

export type AllowedScopes<T extends PermissionType> = PermissionDefinitions[T]['entities'][number]

export type PermissionDefinitions = typeof PERMISSION_DEFINITION
export type PermissionType = keyof typeof PERMISSION_DEFINITION

// Refer to permissions/README.md for information on adding and editing permissions.

export const PERMISSION_DEFINITION = {
  // #region Account
  'account:save': {
    category: 'Account',
    label: 'Edit Accounts',
    action: 'edit and archive accounts, including changing account owners',
    entities: ['none', 'self', 'downline', 'company'],
  } satisfies PermissionDefinition,
  'account:add': {
    category: 'Account',
    label: 'Add Accounts',
    action: 'create accounts',
    entities: ['none', 'self', 'downline', 'company'],
  } satisfies PermissionDefinition,
  'account:manage': {
    category: 'Account',
    label: 'Manage Accounts',
    action: 'manage account activity history and location details, delete accounts, and bypass workflow conditions',
    entities: ['none', 'self', 'downline', 'company'],
  } satisfies PermissionDefinition,
  'account:import': {
    category: 'Account',
    label: 'Import Accounts',
    action: 'import accounts in bulk',
    entities: ['none', 'downline', 'company'],
  } satisfies PermissionDefinition,
  'account:list': {
    category: 'Account',
    label: 'View Accounts',
    action: 'view accounts and their account activity',
    additionalDescription: ' (account stage visibility settings may also grant access independently of this scope)',
    entities: ['self', 'downline', 'withinRestrictions', 'company'],
  } satisfies PermissionDefinition,
  // #endregion

  // #region Settings
  'accountStatus:list': {
    deprecated: true,
    category: 'Settings',
    label: 'View Account Stages',
    action: 'View all account stages and workflows',
    entities: ['company'],
  } satisfies PermissionDefinition,
  'accountStatus:save': {
    category: 'Settings',
    label: 'Manage Account Stages',
    action: 'create, edit, and archive account stages',
    entities: ['none', 'company'],
  } satisfies PermissionDefinition,

  'accountRule:save': {
    category: 'Settings',
    label: 'Account Assignment',
    action: 'assign imported accounts',
    entities: ['none', 'downline', 'company'],
  } satisfies PermissionDefinition,

  'aiChat:list': {
    category: 'Terros AI',
    label: 'Use Terros AI',
    action: 'access Terros AI chat',
    entities: ['none', 'self'],
    featureId: 'Feature:TerrosAIChat',
  } satisfies PermissionDefinition,

  'apikey:manage': {
    category: 'Settings',
    label: 'Manage API Keys',
    action: 'create, edit, and delete API keys',
    entities: ['none', 'self', 'company'],
  } satisfies PermissionDefinition,

  'companyCard:save': {
    category: 'Card',
    label: 'Manage Company MyCard',
    action: 'edit the company digital business card and attachment library',
    entities: ['company'],
  } satisfies PermissionDefinition,

  'userCard:save': {
    category: 'Card',
    label: 'Manage User MyCards',
    action: 'edit user digital business cards',
    entities: ['self', 'downline', 'company'],
  } satisfies PermissionDefinition,

  'company:add': {
    internal: true,
    category: 'Company',
    label: 'Add Company',
    action: 'create a new company account',
    entities: [],
  } satisfies PermissionDefinition,
  'company:delete': {
    internal: true,
    category: 'Company',
    label: 'Delete Company',
    action: 'permanently delete a company account',
    entities: [], // only for terros tech
  } satisfies PermissionDefinition,
  'company:read': {
    internal: true,
    category: 'Company',
    label: 'View Company',
    action: 'view the company account and settings',
    entities: ['company'],
  } satisfies PermissionDefinition,
  'company:update': {
    category: 'Settings',
    label: 'Manage Company Settings',
    action: 'manage the company account settings',
    entities: ['none', 'company'],
  } satisfies PermissionDefinition,
  'company:list': {
    internal: true,
    category: 'Settings',
    label: 'View All Companies',
    action: 'view all companies (only for Terros staff)',
    entities: [],
  } satisfies PermissionDefinition,

  'customField:list': {
    deprecated: true,
    category: 'Settings',
    label: 'View Custom Fields',
    action: 'view custom fields',
    entities: ['company'],
  } satisfies PermissionDefinition,
  'customField:save': {
    category: 'Settings',
    label: 'Manage Custom Fields',
    action: 'create, edit, and archive custom fields',
    entities: ['none', 'downline', 'company'],
  } satisfies PermissionDefinition,

  'app:list': {
    category: 'Settings',
    label: 'View Connect Apps',
    action: 'view Terros Connect apps',
    entities: ['none', 'company'],
  } satisfies PermissionDefinition,
  'app:manage': {
    category: 'Settings',
    label: 'Manage Connect Apps',
    action: 'create, edit, publish, and duplicate Terros Connect apps, versions, and scripts',
    entities: ['none', 'company'],
  } satisfies PermissionDefinition,
  'install:manage': {
    category: 'Settings',
    label: 'Manage Connect Installations',
    action: 'view, create, configure, and remove Terros Connect installations and install secrets',
    entities: ['none', 'company'],
  } satisfies PermissionDefinition,

  'event:outcome:save': {
    category: 'Settings',
    label: 'Manage Closer Outcomes',
    action: 'create, edit, and archive closer event outcomes',
    entities: ['none', 'company'],
  } satisfies PermissionDefinition,
  'event:outcome:list': {
    deprecated: true,
    category: 'Settings',
    label: 'View Closer Outcome Types',
    action: 'view closer event outcome types',
    entities: ['company'],
  } satisfies PermissionDefinition,

  'export:manage': {
    category: 'Settings',
    label: 'Manage Exports',
    action: 'create, edit, and archive exports',
    entities: ['none', 'downline', 'company'],
  } satisfies PermissionDefinition,

  'reportConfig:manage': {
    category: 'Reports',
    label: 'Manage Reports',
    action: 'create, edit, and delete saved report configurations',
    entities: ['none', 'self', 'downline', 'company'],
  } satisfies PermissionDefinition,

  'ratio:list': {
    deprecated: true,
    category: 'Settings',
    label: 'View Ratios',
    action: 'view custom ratios',
    entities: ['company'],
  } satisfies PermissionDefinition,
  'ratio:save': {
    category: 'Settings',
    label: 'Manage Custom Ratios',
    action: 'create, edit, and archive custom ratios',
    entities: ['none', 'company'],
  } satisfies PermissionDefinition,

  'webhook:manage': {
    category: 'Settings',
    label: 'Manage Webhooks',
    action: 'create, edit, and archive webhooks',
    entities: ['none', 'company'],
  } satisfies PermissionDefinition,

  'workflow:list': {
    deprecated: true,
    category: 'Settings',
    label: 'View Workflows',
    action: 'view workflows',
    entities: ['downline', 'company'],
  } satisfies PermissionDefinition,
  'workflow:manage': {
    category: 'Settings',
    label: 'Manage Workflows',
    action: 'create, edit, and archive workflows',
    entities: ['none', 'downline', 'company'],
  } satisfies PermissionDefinition,

  'quiz:manage': {
    category: 'Settings',
    label: 'Manage Quizzes',
    action: 'create, edit, and archive quizzes',
    entities: ['none', 'self'],
  } satisfies PermissionDefinition,
  'quiz:list': {
    category: 'Settings',
    label: 'List Quizzes',
    action: 'view quizzes',
    entities: ['none', 'self', 'company'],
  } satisfies PermissionDefinition,

  'stats:list': {
    category: 'Settings',
    label: 'View Stats',
    action: 'view stats',
    entities: ['none', 'self', 'downline', 'withinRestrictions', 'company'],
  } satisfies PermissionDefinition,
  // #endregion

  // #region Prospecting
  /** effectively area:save */
  'area:add': {
    category: 'Prospecting',
    label: 'Area Management',
    action: 'create, edit, and remove areas',
    entities: ['none', 'downline', 'company'],
  } satisfies PermissionDefinition,
  'area:list': {
    category: 'Prospecting',
    label: 'View Area Assignments',
    action: 'view area assignments',
    entities: ['self', 'downline', 'company'],
  } satisfies PermissionDefinition,

  'contact:list': {
    category: 'Prospecting',
    label: 'View Disposition Pins in Canvass',
    action: 'view disposition pins',
    entities: ['self', 'downline', 'withinRestrictions', 'company'],
  } satisfies PermissionDefinition,
  'contact:add': {
    category: 'Prospecting',
    label: 'Create Disposition',
    action: 'disposition a home',
    entities: ['self', 'company'],
  } satisfies PermissionDefinition,
  'contact:delete': {
    category: 'Prospecting',
    label: 'Delete Disposition',
    action: 'permanently delete a disposition for a home',
    entities: ['self', 'company'],
  } satisfies PermissionDefinition,
  'contact:import': {
    deprecated: true,
    category: 'Prospecting',
    label: 'Import Disposition Data',
    action: 'import disposition data in bulk',
    entities: ['none', 'downline', 'company'],
  } satisfies PermissionDefinition,

  'disposition:save': {
    category: 'Prospecting',
    label: 'Create/Update Dispositions',
    action: 'create and edit dispositions',
    entities: ['none', 'self', 'downline', 'company'],
  } satisfies PermissionDefinition,

  'location:add': {
    category: 'Prospecting',
    label: 'Create Custom Pin',
    action: 'create a custom pin',
    additionalDescription: ' if one does not already exist',
    entities: ['none', 'company'],
  } satisfies PermissionDefinition,

  'location:manage': {
    internal: true,
    category: 'Prospecting',
    label: 'Batch Geocoding',
    action: 'batch geocode properties',
    entities: ['none', 'company'],
  } satisfies PermissionDefinition,

  'tag:manage': {
    category: 'Prospecting',
    label: 'Manage Tags',
    action: 'add tags to accounts, create, edit, and archive tags',
    entities: ['none', 'self'],
  } satisfies PermissionDefinition,

  // #endregion

  // #region Calendar
  'attachment:manage': {
    category: 'Calendar',
    label: 'Calendar Event Attachments',
    action: 'add and delete calendar attachments',
    entities: ['self', 'downline', 'company'],
  } satisfies PermissionDefinition,
  'attachment:list': {
    category: 'Calendar',
    label: 'View Attachments',
    action: 'view attachments',
    entities: ['none', 'company'],
  } satisfies PermissionDefinition,

  'event:read': {
    category: 'Calendar',
    label: 'Calendar Event Visibility',
    action: 'view calendar events',
    entities: ['self', 'downline', 'company'],
  } satisfies PermissionDefinition,
  'event:save': {
    category: 'Calendar',
    label: 'Create and Update Calendar Events',
    action: 'create and update calendar events',
    entities: ['self', 'downline', 'company'],
  } satisfies PermissionDefinition,
  'event:delete': {
    category: 'Calendar',
    label: 'Delete Calendar Events',
    action: 'delete shadows, 1:1s, and personal comebacks',
    entities: ['self', 'downline', 'company'],
  } satisfies PermissionDefinition,
  'event:import': {
    category: 'Calendar',
    label: 'Import Calendar Events',
    action: 'import events in bulk',
    entities: ['none', 'downline', 'company'],
  } satisfies PermissionDefinition,
  'event:manage': {
    category: 'Calendar',
    label: 'Manage Calendar Events',
    action: 'assign and reassign closers, update statuses and closer outcomes, and delete events',
    entities: ['none', 'self', 'downline', 'company'],
  } satisfies PermissionDefinition,

  'availability:list': {
    category: 'Calendar',
    label: 'View Closer Availability',
    action: 'view closer availability',
    entities: ['downline', 'company'],
  } satisfies PermissionDefinition,
  'availability:update': {
    category: 'Calendar',
    label: 'Update Closer Availability',
    action: `update a closer's availability`,
    entities: ['none', 'self', 'downline', 'company'],
  } satisfies PermissionDefinition,
  'availability:manage': {
    category: 'Calendar',
    label: 'Manage Closer Availability Restrictions',
    action: 'change the date or duration of close events and schedule closers outside of their set availability',
    additionalDescription: '. Without this, users can still reschedule by selecting a closer and an available timeslot',
    entities: ['none', 'downline', 'company'],
  } satisfies PermissionDefinition,
  // #endregion

  // #region Pitch
  'pitch:record': {
    deprecated: true,
    category: 'Pitch',
    label: 'Record Pitch',
    action: 'record a pitch',
    entities: ['self', 'downline'],
  } satisfies PermissionDefinition,
  'pitch:delete': {
    deprecated: true,
    category: 'Pitch',
    label: 'Delete Pitch',
    action: 'delete pitches',
    entities: ['downline'],
  } satisfies PermissionDefinition,
  // #endregion

  // #region Evaluation
  'evaluation:add': {
    category: 'Evaluation',
    label: 'Submit Pitch for Evaluation',
    action: 'submit a pitch for evaluation',
    entities: ['self', 'downline', 'company'],
  } satisfies PermissionDefinition,
  'evaluation:delete': {
    category: 'Evaluation',
    label: 'Delete Pitch Evaluation',
    action: 'delete pitch evaluations',
    entities: ['none', 'self', 'downline', 'company'],
  } satisfies PermissionDefinition,
  'evaluation:list': {
    category: 'Evaluation',
    label: 'View Evaluation Score',
    action: 'view evaluation scores',
    entities: ['self', 'downline', 'company'],
  } satisfies PermissionDefinition,
  // #endregion

  // #region Messages
  'message:archive': {
    deprecated: true,
    category: 'Messages',
    label: 'Archive Message',
    action: 'archive messages',
    entities: ['self'],
  } satisfies PermissionDefinition,
  'message:delete': {
    category: 'Messages',
    label: 'Delete Messages',
    action: 'permanently delete messages',
    entities: ['none', 'self', 'company'],
  } satisfies PermissionDefinition,
  'message:send': {
    category: 'Messages',
    label: 'Send Direct Messages',
    action: 'send direct messages',
    entities: ['downline', 'company'],
  } satisfies PermissionDefinition,
  'message:send:group': {
    category: 'Messages',
    label: 'Send Group Messages',
    action: 'send group messages',
    entities: ['downline', 'company'],
  } satisfies PermissionDefinition,
  // #endregion

  // #region Goals & Plans
  /** this also allows updating plans, effectively plan:save */
  'plan:add': {
    category: 'Goals & Plans',
    label: 'Create/Update Quarterly & Weekly Plans',
    action: 'create and update quarterly and weekly plans',
    entities: ['self', 'downline', 'company'],
  } satisfies PermissionDefinition,
  'plan:delete': {
    category: 'Goals & Plans',
    label: 'Delete Quarterly & Weekly Plans',
    action: 'permanently delete quarterly and weekly plans',
    additionalDescription: ' for the company',
    entities: ['none', 'company'],
  } satisfies PermissionDefinition,
  'plan:list': {
    category: 'Goals & Plans',
    label: 'View Quarterly & Weekly Plans',
    action: 'view quarterly and weekly plans',
    entities: ['self', 'downline', 'company'],
  } satisfies PermissionDefinition,
  'plan:manage': {
    category: 'Goals & Plans',
    label: 'Manage Quarterly Plans',
    action: 'approve or reject quarterly plans',
    entities: ['none', 'downline', 'company'],
  } satisfies PermissionDefinition,

  'widget:manage': {
    category: 'Goals & Plans',
    label: 'Manage Widgets',
    action: 'create, update, and delete widgets',
    entities: ['none', 'downline', 'company'],
  } satisfies PermissionDefinition,
  'widget:list': {
    category: 'Goals & Plans',
    label: 'View Widgets',
    action: 'view widgets',
    entities: ['none', 'downline', 'company'],
  } satisfies PermissionDefinition,

  'dashboard:manage': {
    category: 'Reports',
    label: 'Manage Dashboards',
    action: 'manage dashboards',
    entities: ['company', 'self', 'downline', 'withinRestrictions', 'none'],
  } satisfies PermissionDefinition,
  'dashboard:list': {
    category: 'Reports',
    label: 'View Dashboards',
    action: 'view dashboards',
    entities: ['company', 'self', 'downline', 'withinRestrictions'],
  } satisfies PermissionDefinition,

  'program:manage': {
    category: 'Goals & Plans',
    label: 'Manage Programs',
    action: 'create, update, and delete programs',
    entities: ['none', 'downline', 'company'],
  } satisfies PermissionDefinition,
  'program:list': {
    category: 'Goals & Plans',
    label: 'View Programs',
    action: 'view programs',
    entities: ['none', 'company'],
  } satisfies PermissionDefinition,

  'question:manage': {
    category: 'Goals & Plans',
    label: 'App startup questions',
    action: 'create, update, and delete app startup questions',
    entities: ['none', 'company'],
  } satisfies PermissionDefinition,
  // #endregion

  // #region Tasks
  'task:list': {
    category: 'Tasks',
    label: 'View Tasks',
    action: 'view tasks',
    entities: ['self', 'downline', 'company'],
  } satisfies PermissionDefinition,
  'task:update': {
    category: 'Tasks',
    label: 'Update Tasks',
    action: 'snooze and ignore tasks',
    entities: ['none', 'self', 'downline', 'company'],
  } satisfies PermissionDefinition,
  'task:manage': {
    category: 'Tasks',
    label: 'Manage Tasks',
    action: 'create, assign, and delete tasks',
    entities: ['none', 'downline', 'company'],
  } satisfies PermissionDefinition,
  'task:import': {
    category: 'Tasks',
    label: 'Import Tasks',
    action: 'import tasks in bulk',
    entities: ['none', 'downline', 'company'],
  } satisfies PermissionDefinition,
  // #endregion

  // #region Teams
  'team:add': {
    category: 'Teams',
    label: 'Create Team',
    action: 'create a new team',
    entities: ['none', 'downline', 'company'],
  } satisfies PermissionDefinition,
  'team:list': {
    category: 'Teams',
    label: 'View Teams',
    action: 'view teams',
    entities: ['downline', 'withinRestrictions', 'company'],
  } satisfies PermissionDefinition,
  'team:update': {
    category: 'Teams',
    label: 'Update Team',
    action: 'add and remove team members and update team settings',
    entities: ['none', 'downline', 'company'],
  } satisfies PermissionDefinition,
  'team:archive': {
    category: 'Teams',
    label: 'Archive Team',
    action: 'archive a team',
    entities: ['none', 'downline', 'company'],
  } satisfies PermissionDefinition,
  // #endregion

  // #region Roles
  'role:manage': {
    category: 'Roles',
    label: 'Manage Roles',
    action: 'create, update, and archive roles',
    entities: ['none', 'company'],
  } satisfies PermissionDefinition,
  'role:list': {
    category: 'Roles',
    label: 'View Roles',
    action: 'view roles',
    entities: ['company'],
  } satisfies PermissionDefinition,
  // #endregion

  // #region Users
  'user:import': {
    category: 'Users',
    label: 'Import Users',
    action: 'bulk import users',
    entities: ['none', 'downline', 'company'],
  } satisfies PermissionDefinition,
  'user:login': {
    category: 'Users',
    label: 'Impersonation',
    action: 'log in as another user',
    additionalDescription: ' (aka Impersonation)',
    entities: ['none', 'downline', 'company'],
  } satisfies PermissionDefinition,
  'user:list': {
    category: 'Users',
    label: 'View Users',
    action: 'view users',
    entities: ['downline', 'withinRestrictions', 'company'],
  } satisfies PermissionDefinition,
  'user:add': {
    category: 'Users',
    label: 'Create User',
    action: 'create a new user',
    entities: ['none', 'downline', 'company'],
  } satisfies PermissionDefinition,
  'user:manage': {
    category: 'Users',
    label: 'Manage User',
    action: `edit a user's permissions, archive a user profile, and change profile fields your company has locked`,
    entities: ['none', 'downline', 'company'],
  } satisfies PermissionDefinition,
  'user:update': {
    category: 'Users',
    label: 'Update User',
    action: `edit a user's profile information`,
    entities: ['self', 'downline', 'company'],
  } satisfies PermissionDefinition,
  // Deprecated: nothing reads this permission any more. Kept defined so stored grants stay valid; removal is a later change.
  'user:developer': {
    category: 'Users',
    label: 'Legacy CLI access (deprecated)',
    action: `use the Terros CLI (no longer enforced)`,
    entities: ['self'],
  } satisfies PermissionDefinition,

  'admin:assign': {
    category: 'Users',
    label: 'Assign Admin Role',
    action: 'assign the admin role to another user',
    entities: ['none', 'company'],
  } satisfies PermissionDefinition,
  // #endregion

  // #region Competitions
  'game:list': {
    category: 'Competitions',
    label: 'View Competitions',
    action: 'view  competitions',
    entities: ['downline', 'company'],
  } satisfies PermissionDefinition,
  'game:save': {
    category: 'Competitions',
    label: 'Save Competitions',
    action: 'create, edit, and archive competitions',
    entities: ['none', 'downline', 'company'],
  } satisfies PermissionDefinition,
  // #endregion

  // #region Recording
  'rubric:list': {
    category: 'Recording',
    label: 'View Rubrics',
    action: 'view rubrics',
    entities: ['none', 'company', 'downline'],
  } satisfies PermissionDefinition,

  'rubric:manage': {
    category: 'Recording',
    label: 'Manage Rubrics',
    action: 'create, update, and delete rubrics',
    entities: ['none', 'company', 'downline'],
  } satisfies PermissionDefinition,

  'conversation:manage': {
    category: 'Recording',
    label: 'Manage Conversations',
    action: 'create, update, and delete conversations',
    entities: ['none', 'self', 'downline', 'company'],
  } satisfies PermissionDefinition,
  'conversation:list': {
    category: 'Recording',
    label: 'View Conversations',
    action: 'view conversations',
    entities: ['none', 'self', 'downline', 'company'],
  } satisfies PermissionDefinition,

  'compliance:manage': {
    category: 'Recording',
    label: 'Manage Compliance',
    action: 'view, update, and delete compliance policies',
    entities: ['none', 'company'],
  } satisfies PermissionDefinition,
  // #endregion

  'externalLink:manage': {
    category: 'External Links',
    label: 'Save External Link',
    action: 'create, update and delete external links',
    entities: ['none', 'downline', 'company'],
  } satisfies PermissionDefinition,
  'externalLink:list': {
    category: 'External Links',
    label: 'View External Link',
    action: 'view external links',
    entities: ['downline', 'company'],
  } satisfies PermissionDefinition,

  'view:manage': {
    category: 'Saved Views',
    label: 'Share Saved Views',
    action: 'share saved views with users, teams, or the company, and change who a saved view is shared with',
    entities: ['none', 'downline', 'company'],
  } satisfies PermissionDefinition,

  'shorts:manage': {
    category: 'Shorts',
    label: 'Manage Shorts',
    action: 'create, update, and delete shorts',
    entities: ['none', 'self', 'downline', 'company'],
  } satisfies PermissionDefinition,
} as const

export const PERMISSION_TYPES = keys(PERMISSION_DEFINITION)
export const PermissionTypeSchema = z.enum(PERMISSION_TYPES)
